import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { ReportCategories } from '../../constants/reportCategories';
import { SelectableReportTypes, reportNames } from '../../constants/reportTypes';
import { reportDescription } from './features/reportFeatures';
import {
  categoryColors,
  categoryDescriptions,
  categoryIcons,
  categoryNames,
  ReportCategoryDefinition,
} from './features/reportCategoryFeatures';

interface ReportCategorySelectorProps {
  visibleCategories: ReportCategoryDefinition[];
  categoryReportTypeIds: SelectableReportTypes[];
  selectedCategoryId: ReportCategories | null;
  selectedReportType: SelectableReportTypes | null;
  onCategoryClick: (categoryId: ReportCategories) => void;
  onReportTypeClick: (reportType: SelectableReportTypes) => void;
}

const ReportCategorySelector = ({
  visibleCategories,
  categoryReportTypeIds,
  selectedCategoryId,
  selectedReportType,
  onCategoryClick,
  onReportTypeClick,
}: ReportCategorySelectorProps) => (
  <Box display="flex" alignItems="flex-start" gap={4} flexWrap="wrap">
    <Box maxWidth={270} width="100%" flexShrink={0}>
      <Typography variant="h6" gutterBottom>
        Report categories
      </Typography>
      <Box display="flex" flexDirection="column" gap={3}>
        {visibleCategories.map((category) => {
          const Icon = categoryIcons[category.id];
          const color = categoryColors[category.id];
          const isSelected = selectedCategoryId === category.id;
          return (
            <Card
              key={category.id}
              variant="outlined"
              sx={{
                borderColor: isSelected ? color : 'divider',
                bgcolor: isSelected ? alpha(color, 0.06) : 'background.paper',
              }}
            >
              <CardActionArea onClick={() => onCategoryClick(category.id)}>
                <CardContent>
                  <Box display="flex" alignItems="flex-start" gap={3}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        bgcolor: alpha(color, 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ color, fontSize: 24 }} />
                    </Box>
                    <Box flex={1} display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography fontWeight="bold">
                          {categoryNames[category.id]}
                        </Typography>
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{ color }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {categoryDescriptions[category.id]}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>

    {selectedCategoryId && (
      <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 3 }} />
    )}
    {selectedCategoryId && (
      <Box flex={1}>
        <Typography variant="h6" gutterBottom>
          {categoryNames[selectedCategoryId]}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={3} alignItems="stretch">
          {categoryReportTypeIds.map((reportType) => {
            const Icon = categoryIcons[selectedCategoryId];
            const color = categoryColors[selectedCategoryId];
            const isSelected = selectedReportType === reportType;
            return (
              <Card
                key={reportType}
                variant="outlined"
                sx={{
                  maxWidth: 230,
                  borderColor: isSelected ? color : 'divider',
                  bgcolor: isSelected ? alpha(color, 0.06) : 'background.paper',
                }}
              >
                <CardActionArea
                  onClick={() => onReportTypeClick(reportType)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ height: '100%' }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            flexShrink: 0,
                            bgcolor: alpha(color, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon sx={{ color, fontSize: 18 }} />
                        </Box>
                        <Typography fontWeight="bold">{reportNames[reportType]}</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {reportDescription[reportType]}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </Box>
    )}
  </Box>
);

export default ReportCategorySelector;
